/**
 * THE DIAGNOSTIC CARD. Phase 10 on the Workflow page.
 *
 * Anthony, 30 September: "build the diagnostic card as its own screen."
 *
 * What the technician found, in their own words, plus the photo of the
 * trouble spot. It is the thing the desk quotes from, so it has to say
 * what is wrong rather than what it looks like, and the desk has to be
 * able to read it back without hunting.
 *
 * WHY IT LEFT THE NOTES. It used to be a line stamped into jobs.notes
 * behind a tag. That worked, and it was hard to read back: the diagnosis
 * sat in one paragraph with gate codes and odometer stamps, a second line
 * of it was dropped the next time the walk prefilled the box, and adding
 * to it meant stamping a second copy. Now it is jobs.diagnosis (0188),
 * one card per job that anybody on the job can open, add to and save.
 *
 * SAVED AS YOU GO, the same as the inspection sheet. A pause in the
 * typing saves it, leaving the box saves it, and the button is there for
 * the person who does not trust either. Who wrote it and when is stamped
 * by the database, not sent from here.
 *
 * THE CUSTOMER READS IT TOO. Anthony, the same afternoon: "show the card
 * on the customer page too." It appears on their garage page under What
 * You Told Us, with its date and never the author's name. One switch here
 * keeps a card off their page. The cards copied out of old notes started
 * hidden (0189), because nobody wrote those for a customer to read.
 */

import {
  getJob, saveDiagnosis, setDiagnosisHidden, vehicleName, type JobRow,
} from "../data/jobs.ts";
import { diagnosisOf } from "../data/tests.ts";
import { db } from "../data/client.ts";
import { esc, mount, since } from "../ui/dom.ts";
import { jobPhotos } from "./photos.ts";
import { shopWords } from "../ui/words.ts";
import type { Role } from "../domain/permissions.ts";

/** How long a pause in typing counts as "done for now". */
const QUIET_MS = 1500;

async function authorName(id: string | null | undefined): Promise<string> {
  if (!id) return "";
  const sb = await db();
  const { data } = await sb.from("profiles").select("full_name").eq("id", id).maybeSingle();
  return (data as { full_name: string | null } | null)?.full_name ?? "";
}

function stampLine(at: string | null | undefined, by: string): string {
  if (!at) return "";
  return `Saved ${esc(since(at))}${by ? ` by ${esc(by)}` : ""}.`;
}

export async function diagnosisPage(jobId: string, role: Role = "viewer"): Promise<void> {
  mount(`<div class="page"><div class="loading">Reading&hellip;</div></div>`);

  let job: JobRow | null;
  try {
    job = await getJob(jobId);
  } catch (e) {
    mount(`<div class="page"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`);
    return;
  }
  if (!job) {
    mount(`<div class="page"><div class="empty">That job is not on your board.</div></div>`);
    return;
  }

  const w = shopWords();
  const car = vehicleName(job.vehicles);
  const text = diagnosisOf(job);
  /* A job from before 0188 whose card is still only the tagged line in
     notes. Said out loud, so nobody wonders why it has no name on it. */
  const fromNotes = !job.diagnosis?.trim() && text !== "";
  const mayEdit = role !== "viewer";
  const by = await authorName(job.diagnosed_by).catch(() => "");

  mount(`<div class="page">
    <a class="back" href="#/job/${esc(jobId)}">&larr; The job</a>
    <header class="page-head">
      <h1>Diagnostic card</h1>
      <p>${esc(`#${job.job_number}`)}${car ? ` &middot; ${esc(car)}` : ""}</p>
    </header>

    ${job.complaint?.trim() ? `<div class="card pad">
      <div class="quiet small">What the customer told us</div>
      <p>${esc(job.complaint)}</p>
    </div>` : ""}

    <div class="card pad">
      ${mayEdit ? `
      <label class="techfield"><span>What is wrong with the ${esc(w.thing)}</span>
        <textarea id="d-text" rows="7" maxlength="4000"
          placeholder="What you found, in your own words. Add to it any time.">${esc(text)}</textarea></label>
      <p class="techfine">The desk prices the work from this, so say what is
        wrong, not what you did to look. Coming back with more? Add a line
        and it saves.</p>
      <p class="techfine" id="d-seen">${job.diagnosis_hidden
        ? "Hidden from the customer. Only the shop can read this card."
        : "The customer reads this on their own page, so write it for them."}</p>
      <div class="ifin">
        <button class="btn primary" id="d-save">Save the card</button>
        <button class="btn ghost" id="d-hide">${job.diagnosis_hidden
          ? "Show it to the customer" : "Hide from the customer"}</button>
        <a class="btn ghost" href="#/inspection/${esc(jobId)}">Open the inspection</a>
      </div>`
      : text
        ? `<p style="white-space:pre-wrap">${esc(text)}</p>`
        : `<p class="quiet">Nothing written on this card yet.</p>`}
      <p class="quiet small" id="d-stamp">${fromNotes
        ? "This came from the job notes, from before the card had its own screen. Saving puts it on the card."
        : stampLine(job.diagnosed_at, by)}</p>
      <div class="msg-slot" id="d-msg"></div>
    </div>

    <!-- The photo of the trouble spot. Filed as "The fault", the same
         kind the walk's step files it as, so both screens show one set. -->
    <div class="card" id="diagphotos"></div>
  </div>`);

  jobPhotos("diagphotos", jobId, job.vehicles?.id ?? null, role, "damage");
  if (!mayEdit) return;

  const box = document.getElementById("d-text") as HTMLTextAreaElement | null;
  const btn = document.getElementById("d-save") as HTMLButtonElement | null;
  const stamp = document.getElementById("d-stamp");
  const msg = document.getElementById("d-msg");
  if (!box || !btn) return;

  /* What the database holds. A save only goes out when the box differs
     from it, so leaving the box after a timed save does not save twice. */
  let saved = job.diagnosis ?? "";
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight: Promise<void> | null = null;

  const save = async (): Promise<void> => {
    if (timer) { clearTimeout(timer); timer = undefined; }
    if (inFlight) await inFlight;
    const now = box.value.trim();
    if (now === saved.trim()) return;
    btn.disabled = true; btn.textContent = "Saving…";
    inFlight = (async () => {
      try {
        const back = await saveDiagnosis(jobId, now);
        saved = back.diagnosis ?? "";
        const who = await authorName(back.diagnosed_by).catch(() => "");
        if (stamp) stamp.innerHTML = saved ? stampLine(back.diagnosed_at, who) : "The card is empty.";
        if (msg) msg.innerHTML = "";
      } catch (e) {
        if (msg) msg.innerHTML = `<p class="msg bad">${
          esc(e instanceof Error ? e.message : String(e))}</p>`;
      } finally {
        btn.disabled = false; btn.textContent = "Save the card";
      }
    })();
    await inFlight;
    inFlight = null;
  };

  box.addEventListener("input", () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void save(), QUIET_MS);
  });
  box.addEventListener("change", () => void save());
  btn.addEventListener("click", () => void save());

  let hidden = Boolean(job.diagnosis_hidden);
  const hide = document.getElementById("d-hide") as HTMLButtonElement | null;
  const seen = document.getElementById("d-seen");
  hide?.addEventListener("click", async () => {
    hide.disabled = true;
    try {
      hidden = await setDiagnosisHidden(jobId, !hidden);
      hide.textContent = hidden ? "Show it to the customer" : "Hide from the customer";
      if (seen) {
        seen.textContent = hidden
          ? "Hidden from the customer. Only the shop can read this card."
          : "The customer reads this on their own page, so write it for them.";
      }
      if (msg) msg.innerHTML = "";
    } catch (e) {
      if (msg) msg.innerHTML = `<p class="msg bad">${
        esc(e instanceof Error ? e.message : String(e))}</p>`;
    } finally {
      hide.disabled = false;
    }
  });
  /* Leaving for another screen mid-sentence. The hash changes before the
     box ever blurs on some phones, so this is the save that catches it. */
  window.addEventListener("hashchange", () => {
    if (timer) void save();
  }, { once: true });
}
